import {
  createEvent,
  createStore,
  createEffect,
  sample,
  Effect
} from 'effector'
import { uploadVideoChunkApiV1InterviewsInterviewIdUploadVideoChunkPost } from 'api/endpoints/openapi-generated'
import { videoRecorderService, UploadChunkData } from './video-recorder.service'
import { captureError } from 'utils/sentry'
import { v4 } from 'uuid'
import { openDB } from 'idb'
import { interviewFixturesService } from 'features/interview/interview-fixtures.service'

type ChunkRecord = {
  interviewId: string
  file: Blob
  uuid: string
  createdAt: number
  isUploaded: boolean
}

const dbPromise = openDB('video-chunks', 1, {
  upgrade(db) {
    db.createObjectStore('chunks', { keyPath: 'uuid' })
  }
})

export class VideoChunksUploadService {
  readonly chunkUploaded = createEvent<void>()
  readonly chunkRemoveAfterUpload = createEvent<ChunkRecord>()
  readonly nextQueue = createEvent<Array<ChunkRecord>>()

  private readonly isPreviewEnabledDevOnly = false
  private readonly allChunks: Array<Blob> = []

  readonly $queue = createStore<ChunkRecord[]>([])
  readonly $isUploading = createStore(false)

  private readonly createChunkRecordFx: Effect<UploadChunkData, ChunkRecord>

  constructor() {
    this.createChunkRecordFx = createEffect(this.createChunkRecord)

    this.attachQueueLogic()
    this.attachIndexedDbPersistLogic()
  }

  private attachQueueLogic(): void {
    sample({
      clock: videoRecorderService.doUploadChunk,
      target: this.createChunkRecordFx
    })

    sample({
      clock: this.createChunkRecordFx.doneData,
      source: this.$queue,
      fn: (queue, chunk: ChunkRecord) => {
        if (window.isDevMode) {
          console.log(
            'VideoChunksUploadService: added new video chunk to upload',
            chunk.uuid,
            'queue length',
            queue.length + 1
          )
        }

        return [...queue, chunk]
      },
      target: this.$queue
    })

    const uploadFx = createEffect(this.uploadFxImpl)

    sample({
      clock: this.$queue,
      source: this.$isUploading,
      filter: (isUploading, queue) => !isUploading && !!queue.length,
      fn: (_, queue) => queue[0],
      target: uploadFx
    })

    this.$isUploading.on(uploadFx, () => true)
    this.$isUploading.on(uploadFx.done, () => false)

    sample({
      clock: uploadFx.done,
      target: this.chunkUploaded
    })

    sample({
      clock: this.chunkUploaded,
      source: this.$queue,
      fn: (queue) => queue[0],
      target: this.chunkRemoveAfterUpload
    })

    sample({
      clock: this.chunkRemoveAfterUpload,
      source: this.$queue,
      fn: (queue, chunk) => queue.filter((x) => x.uuid !== chunk.uuid),
      target: this.nextQueue
    })

    sample({
      clock: this.nextQueue,
      target: this.$queue
    })
  }

  private get isSaveRecordingChunks(): boolean {
    return !!window.isDevMode && !!window.isSaveRecordingChunks
  }

  private attachIndexedDbPersistLogic = (): void => {
    sample({
      clock: this.createChunkRecordFx.doneData,
      target: createEffect(this.addChunkToIndexedDb)
    })

    const loadedChunksFx = createEffect(this.loadChunksFromIndexedDb)

    sample({
      clock: loadedChunksFx.doneData,
      source: this.$queue,
      fn: (queue: Array<ChunkRecord>, loadedChunks: Array<ChunkRecord>) => {
        const unique = new Map<string, ChunkRecord>()

        loadedChunks.concat(queue).forEach((x) => {
          unique.set(x.uuid, x)
        })

        return Array.from(unique.values()).sort((a, b) => {
          return a.createdAt - b.createdAt
        })
      },
      target: this.$queue
    })

    sample({
      clock: interviewFixturesService.$interviewId,
      filter: Boolean,
      target: loadedChunksFx
    })

    sample({
      clock: this.nextQueue,
      filter: (queue) => !queue.length,
      target: createEffect(() => {
        if (this.isSaveRecordingChunks) return
        return this.rmAllChunks()
      })
    })
  }

  private createChunkRecord = (chunk: UploadChunkData): ChunkRecord => {
    return {
      interviewId: chunk.interviewId,
      file: chunk.file,
      uuid: v4(),
      createdAt: Date.now(),
      isUploaded: false
    }
  }

  private addChunkToIndexedDb = async (chunk: ChunkRecord): Promise<void> => {
    if (window.isDevMode) {
      console.log('added chunk to indexed db', chunk.uuid)
    }

    const db = await dbPromise
    await db.put('chunks', chunk)
  }

  private setChunkAsUploadedInDb = async (
    chunk: ChunkRecord
  ): Promise<void> => {
    const db = await dbPromise

    const chunkToWrite: ChunkRecord = {
      ...chunk,
      isUploaded: true
    }

    await db.put('chunks', chunkToWrite)
  }

  private deleteChunkFromIndexedDb = async (
    chunk: ChunkRecord
  ): Promise<void> => {
    const db = await dbPromise
    await db.delete('chunks', chunk.uuid)
  }

  private loadChunksFromIndexedDb = async (
    interviewId: string
  ): Promise<Array<ChunkRecord>> => {
    const db = await dbPromise
    const chunks = (await db.getAll('chunks')) as Array<ChunkRecord>
    return chunks.filter((x) => x.interviewId === interviewId && !x.isUploaded)
  }

  private rmAllChunks = async (): Promise<void> => {
    const db = await dbPromise
    await db.clear('chunks')
  }

  private previewChunk = async (lastChunkBlob: Blob) => {
    if (!this.isPreviewEnabledDevOnly) return

    this.allChunks.push(lastChunkBlob)

    const blob = new Blob(this.allChunks, {
      type: 'video/webm;codecs=vp8,opus'
    })

    const url = URL.createObjectURL(blob)

    document.getElementById('aboba')?.remove()

    const video = document.createElement('video')
    video.id = 'aboba'

    video.controls = true
    video.autoplay = false
    video.src = url

    document.body.appendChild(video)
  }

  private uploadFxImpl = async (
    params: ChunkRecord,
    attempt = 1
  ): Promise<void> => {
    const maxTries = 3
    const { interviewId, file, uuid } = params

    try {
      // optimistically set to prevent upload duplicates
      void this.setChunkAsUploadedInDb(params)

      await uploadVideoChunkApiV1InterviewsInterviewIdUploadVideoChunkPost(
        interviewId,
        { file }
      )

      if (this.isSaveRecordingChunks) {
        console.log('VideoChunksUploadService: uploaded video chunk', uuid)
      } else {
        void this.deleteChunkFromIndexedDb(params)
      }

      void this.previewChunk(file)
    } catch (e) {
      if (attempt < maxTries) {
        return this.uploadFxImpl(params, attempt + 1)
      } else {
        const logInfo = `VideoChunksUploadService: upload video record chunk not successful (try 3/3) ${uuid} ${(
          e as Error
        )?.toString()}`

        console.error(logInfo)
        captureError(logInfo)
      }
    }
  }

  subscribe = (): VoidFunction => {
    return () => void 0
  }
}

export const videoChunksUploadService = new VideoChunksUploadService()

async function getChunksFromIndexedDb(
  interviewId: string | undefined,
  uploadedOnly: boolean,
  limit: number | undefined,
  argOnlyChunks?: Set<string>
): Promise<Array<ChunkRecord>> {
  const db = await dbPromise
  const all = (await db.getAll('chunks')) as ChunkRecord[]

  // фильтрация по interviewId (если указан)
  const rawChunks = interviewId
    ? all.filter((c) => c.interviewId === interviewId)
    : all

  if (!rawChunks.length) {
    console.warn('Нет чанков для сборки', { interviewId })
    return []
  }

  const ignoredChunks = new Set<string>([])

  const onlyChunks = argOnlyChunks || new Set<string>([])

  const filteredChunks = rawChunks
    .filter((c) => {
      if (ignoredChunks.has(c.uuid)) return false
      if (onlyChunks.size && !onlyChunks.has(c.uuid)) return false

      return !uploadedOnly || c.isUploaded
    })
    .sort((a, b) => a.createdAt - b.createdAt)

  const slicedChunks = filteredChunks.slice(0, limit || filteredChunks.length)

  console.log(
    `chunks to create video from (${slicedChunks.length})\n`,
    slicedChunks.map((x) => `${x.uuid} ${x.file.size}`).join('\n'),
    '\nall chunks ',
    rawChunks.length
  )

  return slicedChunks
}

async function buildVideoFromIndexedDBDevOnly(
  interviewId: string | undefined,
  uploadedOnly: boolean,
  limit: number,
  argOnlyChunks?: Set<string>
): Promise<{ blob: Blob; url: string }> {
  const slicedChunks = await getChunksFromIndexedDb(
    interviewId,
    uploadedOnly,
    limit,
    argOnlyChunks
  )

  // объединяем
  const blob = getBlob(slicedChunks)

  // создаём URL
  const url = URL.createObjectURL(blob)

  // показываем в плеере
  document.getElementById('dev-full-video')?.remove()
  const video = document.createElement('video')
  video.id = 'dev-full-video'
  video.controls = true
  video.src = url
  video.style.width = '640px'
  document.body.appendChild(video)

  console.log('Собранное видео', {
    interviewId,
    chunks: slicedChunks.length,
    size: blob.size,
    url
  })

  return { blob, url }
}

async function downloadChunkByIds(uuids: Array<string>): Promise<void> {
  const db = await dbPromise

  for (const uuid of uuids) {
    const chunk = (await db.get('chunks', uuid)) as ChunkRecord | undefined

    if (!chunk) {
      console.warn(`Chunk ${uuid} not found in IndexedDB`)
      return
    }

    const blob = chunk.file
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = `${uuid}.webm` // можно поменять на .mp4 если контейнер другой
    a.style.display = 'none'

    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)

    URL.revokeObjectURL(url)

    console.log(`Chunk ${uuid} downloaded`, { size: blob.size })
  }
}

function getBlob(chunks: Array<ChunkRecord>): Blob {
  return new Blob(
    chunks.map((c) => c.file),
    {
      type: 'video/webm;codecs=vp8,opus'
    }
  )
}

// async function concatWithMpeg() {
//   const one = await getChunksFromIndexedDb(
//     void 0,
//     false,
//     void 0,
//     new Set([
//       '165bd7f5-a77f-4a6c-880e-d7ea78c44cd3',
//       '076fe129-92cf-43d4-b06b-ef81a8d009cd',
//       'c747722a-28d2-457b-ab63-1fd338751140'
//     ])
//   )
//
//   const oneBlob = getBlob(one)
//
//   const two = await getChunksFromIndexedDb(
//     void 0,
//     false,
//     void 0,
//     new Set([
//       '59846ce0-4b45-437e-afab-87f514d05c51',
//       '72782abb-2427-4716-85bb-7099f1272c2a',
//       'df602048-0d2d-4720-8b92-9ed9baee7524'
//     ])
//   )
//
//   const twoBlob = getBlob(two)
//
//   const finalBlob = await buildFinalRecording([oneBlob, twoBlob])
//
//   const url = URL.createObjectURL(finalBlob)
//   const video = document.createElement('video')
//   video.controls = true
//   video.src = url
//   document.body.appendChild(video)
// }

// async function buildFinalRecording(blobs: Blob[]): Promise<Blob> {
//   const ffmpeg = new FFmpeg()
//   ffmpeg.on('log', ({ message }) => console.log('ffmpeg:', message))
//
//   const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/esm'
//   await ffmpeg.load({
//     coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
//     wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm')
//   })
//
//   // шаг 1: пишем все блобы и ремапим дорожки
//   const fixedFiles: string[] = []
//   for (let i = 0; i < blobs.length; i++) {
//     const inFile = `in${i}.webm`
//     const outFile = `fixed${i}.webm`
//
//     await ffmpeg.writeFile(inFile, await fetchFile(blobs[i]))
//     await ffmpeg.exec([
//       '-i',
//       inFile,
//       '-map',
//       '0:v',
//       '-map',
//       '0:a',
//       '-c',
//       'copy',
//       outFile
//     ])
//
//     fixedFiles.push(outFile)
//   }
//
//   // шаг 2: создаём list.txt для конкатенации
//   const list = fixedFiles.map((f) => `file '${f}'`).join('\n')
//   await ffmpeg.writeFile('list.txt', list)
//
//   // шаг 3: конкат
//   await ffmpeg.exec([
//     '-f',
//     'concat',
//     '-safe',
//     '0',
//     '-i',
//     'list.txt',
//     '-c',
//     'copy',
//     'output.webm'
//   ])
//
//   // шаг 4: читаем результат
//   const data = await ffmpeg.readFile('output.webm')
//   return new Blob([data], { type: 'video/webm' })
// }

// godWindow.buildFullRecording = concatWithMpeg

window.buildVideoFromIndexedDBDevOnly = buildVideoFromIndexedDBDevOnly
window.downloadChunkByIdsDevOnly = downloadChunkByIds
